import { useEffect, useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Play, Timer, X, ChevronRight, CheckSquare, Flag, Pencil, Paperclip, FileText, Loader2, Trash2 } from "lucide-react";

const mmss = (secs) => {
  const neg = secs < 0;
  const s = Math.abs(Math.round(secs));
  return `${neg ? "-" : ""}${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};
const fmtD = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("en-AU", { day: "2-digit", month: "short" }) : "—");
const fmtVal = (v, unit) => {
  if (v === null || v === undefined) return "—";
  if (unit === "currency") return Math.abs(v) >= 10000 ? `$${Math.round(v / 1000)}k` : `$${Math.round(v).toLocaleString()}`;
  if (unit === "percent") return `${v}%`;
  return v;
};
const ROCK_STATUS = {
  on_track: { label: "On track", cls: "bg-emerald-100 text-emerald-700", next: "off_track" },
  off_track: { label: "Off track", cls: "bg-red-100 text-red-700", next: "done" },
  done: { label: "Done", cls: "bg-slate-200 text-slate-700", next: "on_track" },
};
const WIDGET_OPTS = [["none", "Discussion only"], ["scorecard", "Scorecard review"], ["rocks", "Rock review"], ["todos", "To-Do review"], ["ids", "IDS — issues"], ["conclude", "Conclude & rate"]];

function ScorecardWidget() {
  const { data } = useQuery({ queryKey: ["mtg-scorecard"], queryFn: async () => (await api.get("/mgmt/scorecard?weeks=3")).data });
  const qc = useQueryClient();
  const issueMut = useMutation({
    mutationFn: async (m) => (await api.post("/mgmt/issues", { title: `Scorecard: ${m.name} off target`, detail: `Raised in L10 — last week ${fmtVal(m.values[m.values.length - 2], m.unit)} vs goal ${m.goal_op === "lte" ? "≤" : "≥"} ${fmtVal(m.goal, m.unit)}` })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mgmt-issues"] }); toast.success("Dropped to Issues"); },
  });
  const wk = data?.weeks?.[data.weeks.length - 2];
  return (
    <div data-testid="widget-scorecard">
      <div className="text-xs text-slate-500 mb-2">Last completed week {wk ? `(${fmtD(wk.start)})` : ""} — call hit or miss, don't solve here.</div>
      {(data?.metrics || []).map((m) => {
        const idx = m.values.length - 2;
        const v = m.values[idx];
        const hit = m.hits[idx];
        return (
          <div key={m.id} className="flex items-center gap-2 py-1.5 border-b border-slate-100 text-sm" data-testid={`mtg-metric-${m.id}`}>
            <span className="flex-1 font-medium text-slate-800">{m.name}</span>
            <span className="font-mono text-xs text-slate-500">{m.goal_op === "lte" ? "≤" : "≥"} {fmtVal(m.goal, m.unit)}</span>
            <span className={`font-mono text-xs px-2 py-0.5 rounded-sm min-w-[52px] text-center ${v == null ? "text-slate-300" : hit ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{fmtVal(v, m.unit)}</span>
            {v != null && !hit && (
              <button className="text-[10px] font-bold text-cyan-700 hover:underline whitespace-nowrap" onClick={() => issueMut.mutate(m)} data-testid={`mtg-metric-issue-${m.id}`}>→ Issue</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RocksWidget() {
  const qc = useQueryClient();
  const { data: qData } = useQuery({ queryKey: ["mgmt-quarters"], queryFn: async () => (await api.get("/mgmt/quarters")).data });
  const { data } = useQuery({
    queryKey: ["mtg-rocks", qData?.current],
    queryFn: async () => (await api.get(`/mgmt/rocks?quarter=${qData.current}`)).data,
    enabled: !!qData?.current,
  });
  const statusMut = useMutation({
    mutationFn: async (r) => (await api.patch(`/mgmt/rocks/${r.id}`, { status: ROCK_STATUS[r.status].next })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mtg-rocks"] }),
  });
  const issueMut = useMutation({
    mutationFn: async (r) => (await api.post("/mgmt/issues", { title: `Rock off track: ${r.title}`, detail: `Owner ${r.owner_name} — dropped from L10 rock review` })).data,
    onSuccess: () => toast.success("Dropped to Issues"),
  });
  return (
    <div data-testid="widget-rocks">
      <div className="text-xs text-slate-500 mb-2">Call it: on track or off track. Off-track rocks drop to Issues — don't solve here.</div>
      {(data?.rocks || []).map((r) => (
        <div key={r.id} className="flex items-center gap-2 py-1.5 border-b border-slate-100 text-sm" data-testid={`mtg-rock-${r.id}`}>
          <span className="flex-1 font-medium text-slate-800">{r.title}</span>
          <span className="text-xs text-slate-400 whitespace-nowrap">{r.owner_name}</span>
          <button onClick={() => statusMut.mutate(r)} data-testid={`mtg-rock-status-${r.id}`}
            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${ROCK_STATUS[r.status]?.cls}`}>
            {ROCK_STATUS[r.status]?.label}
          </button>
          {r.status === "off_track" && (
            <button className="text-[10px] font-bold text-cyan-700 hover:underline whitespace-nowrap" onClick={() => issueMut.mutate(r)} data-testid={`mtg-rock-issue-${r.id}`}>→ Issue</button>
          )}
        </div>
      ))}
      {(data?.rocks || []).length === 0 && <div className="text-sm text-slate-400 py-4">No rocks this quarter.</div>}
    </div>
  );
}

function TodosWidget() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["mgmt-todos"], queryFn: async () => (await api.get("/mgmt/todos")).data });
  const doneMut = useMutation({
    mutationFn: async (t) => (await api.patch(`/mgmt/todos/${t.id}`, { status: t.status === "done" ? "open" : "done" })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mgmt-todos"] }); qc.invalidateQueries({ queryKey: ["mtg-todos"] }); qc.invalidateQueries({ queryKey: ["daily-tasks"] }); qc.invalidateQueries({ queryKey: ["activities"] }); },
  });
  const rows = [...(data?.open || []), ...(data?.done || []).slice(0, 5)];
  return (
    <div data-testid="widget-todos">
      <div className="text-xs text-slate-500 mb-2">Last week's to-dos — done or not done. 90% done is the standard.</div>
      {rows.map((t) => (
        <label key={t.id} className="flex items-center gap-2 py-1.5 border-b border-slate-100 text-sm cursor-pointer" data-testid={`mtg-todo-${t.id}`}>
          <input type="checkbox" checked={t.status === "done"} onChange={() => doneMut.mutate(t)} className="w-4 h-4 accent-emerald-600" data-testid={`mtg-todo-check-${t.id}`} />
          <span className={`flex-1 ${t.status === "done" ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</span>
          <span className="text-xs text-slate-400">{t.owner_name}</span>
          <span className="font-mono text-[10px] text-slate-400">{fmtD(t.due_date)}</span>
        </label>
      ))}
      {rows.length === 0 && <div className="text-sm text-slate-400 py-4">No to-dos yet.</div>}
    </div>
  );
}

function IdsWidget() {
  const qc = useQueryClient();
  const [solving, setSolving] = useState(null); // {id, number, title, solution, owner_id, owner_name, due_date}
  const { data } = useQuery({ queryKey: ["mgmt-issues"], queryFn: async () => (await api.get("/mgmt/issues")).data });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/auth/team")).data });
  const plus7 = () => new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const patchMut = useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/mgmt/issues/${id}`, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mgmt-issues"] }),
  });
  const actionMut = useMutation({
    mutationFn: async () => (await api.post(`/mgmt/issues/${solving.id}/action`, {
      solution: solving.solution.trim(), owner_id: solving.owner_id,
      owner_name: solving.owner_name, due_date: solving.due_date || null,
    })).data,
    onSuccess: () => {
      ["mgmt-issues", "mgmt-todos", "mtg-todos", "daily-tasks", "activities"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      setSolving(null);
      toast.success("Action assigned — the issue solves itself when the to-do is done");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not assign action"),
  });
  return (
    <div data-testid="widget-ids">
      <div className="text-xs text-slate-500 mb-2">Top-voted first. Identify the real issue, discuss once each, then assign the action to an owner — the issue closes itself when their to-do is done.</div>
      {(data?.open || []).map((i) => (
        <div key={i.id} className="py-2 border-b border-slate-100" data-testid={`mtg-issue-${i.id}`}>
          <div className="flex items-center gap-2 text-sm">
            <span className="font-mono text-cyan-700 text-[10px] whitespace-nowrap">{i.number || "—"}</span>
            <span className="flex-1 font-medium text-slate-800">{i.title}</span>
            <span className="font-mono text-[10px] text-slate-400">▲ {i.vote_count}</span>
            {i.status === "identified" && (
              <button className="text-[10px] font-bold uppercase px-2 py-1 rounded-sm bg-amber-100 text-amber-700" data-testid={`mtg-issue-discuss-${i.id}`}
                onClick={() => patchMut.mutate({ id: i.id, body: { status: "discussing" } })}>Discuss</button>
            )}
            <button className="text-[10px] font-bold uppercase px-2 py-1 rounded-sm bg-emerald-100 text-emerald-700" data-testid={`mtg-issue-solve-${i.id}`}
              onClick={() => setSolving({ id: i.id, number: i.number, title: i.title, solution: i.action?.title || "", owner_id: "", owner_name: "", due_date: plus7() })}>
              {i.action ? "Re-assign" : "Solve"}
            </button>
          </div>
          {i.action ? (
            <div className="text-xs text-slate-500 ml-6 mt-0.5 line-clamp-1" data-testid={`mtg-issue-action-${i.id}`}>
              → {i.action.title} · <span className="text-slate-400">{i.action.owner_name} · {i.action.status === "done" ? "✓ done" : "open"}</span>
            </div>
          ) : (
            i.detail && <div className="text-xs text-slate-400 ml-6 mt-0.5 line-clamp-1">{i.detail}</div>
          )}
        </div>
      ))}
      {(data?.open || []).length === 0 && <div className="text-sm text-slate-400 py-4">Issues list is clear. 🔨 Back to work.</div>}
      <Dialog open={!!solving} onOpenChange={(o) => !o && setSolving(null)}>
        <DialogContent className="max-w-sm rounded-sm" data-testid="solve-dialog">
          <DialogHeader><DialogTitle className="text-base">Solve {solving?.number}: {solving?.title}</DialogTitle></DialogHeader>
          <Textarea rows={2} placeholder="Action agreed — this becomes the owner's to-do" value={solving?.solution || ""}
            onChange={(e) => setSolving((s) => ({ ...s, solution: e.target.value }))} className="rounded-sm" data-testid="solve-input" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={solving?.owner_id || ""} onValueChange={(v) => {
              const m = (team || []).find((t) => t.id === v);
              setSolving((s) => ({ ...s, owner_id: v, owner_name: m?.name || "" }));
            }}>
              <SelectTrigger className="rounded-sm" data-testid="solve-owner-select"><SelectValue placeholder="Assign to *" /></SelectTrigger>
              <SelectContent>{(team || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={solving?.due_date || ""} onChange={(e) => setSolving((s) => ({ ...s, due_date: e.target.value }))} className="rounded-sm" data-testid="solve-due-input" />
          </div>
          <p className="text-[10.5px] text-slate-400">Lands in the owner's Daily Tasks as "{solving?.number} — …" — the issue flips to Solved when they tick it done.</p>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setSolving(null)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="solve-confirm-btn"
              disabled={!solving?.solution?.trim() || !solving?.owner_id || actionMut.isPending}
              onClick={() => actionMut.mutate()}>Assign Action</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function LiveMeeting({ meeting, team }) {
  const qc = useQueryClient();
  const [now, setNow] = useState(Date.now());
  const [concluding, setConcluding] = useState(false);
  const [ratings, setRatings] = useState({});
  const [notes, setNotes] = useState(meeting.notes || "");
  const [todoForm, setTodoForm] = useState({ title: "", owner_id: "", owner_name: "" });
  const [segNotes, setSegNotes] = useState(meeting.segment_notes || {});

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const { data: mtgTodos } = useQuery({
    queryKey: ["mtg-todos", meeting.id],
    queryFn: async () => (await api.get(`/mgmt/todos?meeting_id=${meeting.id}`)).data,
  });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["mgmt-meetings"] }); };

  const segMut = useMutation({
    mutationFn: async (idx) => (await api.patch(`/mgmt/meetings/${meeting.id}`, { current_segment: idx })).data,
    onSuccess: invalidate,
  });
  const todoMut = useMutation({
    mutationFn: async () => (await api.post("/mgmt/todos", { ...todoForm, owner_id: todoForm.owner_id || null, meeting_id: meeting.id })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mtg-todos", meeting.id] }); qc.invalidateQueries({ queryKey: ["mgmt-todos"] }); qc.invalidateQueries({ queryKey: ["daily-tasks"] }); qc.invalidateQueries({ queryKey: ["activities"] }); setTodoForm({ title: "", owner_id: "", owner_name: "" }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not add to-do"),
  });
  const concludeMut = useMutation({
    mutationFn: async () => {
      await api.patch(`/mgmt/meetings/${meeting.id}`, { segment_notes: segNotes }).catch(() => {});
      const r = Object.entries(ratings).filter(([, v]) => v).map(([id, v]) => {
        const t = (team || []).find((x) => x.id === id);
        return { user_id: id, name: t?.name || "Staff", rating: Number(v) };
      });
      return (await api.post(`/mgmt/meetings/${meeting.id}/conclude`, { ratings: r, notes: notes || null })).data;
    },
    onSuccess: (d) => { invalidate(); setConcluding(false); toast.success(`Meeting saved — ${d.duration_min} min${d.avg_rating ? `, rated ${d.avg_rating}/10` : ""}`); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not conclude"),
  });

  const segs = meeting.segments;
  const cur = meeting.current_segment;
  const seg = segs[cur];
  const elapsedTotal = (now - new Date(meeting.started_at).getTime()) / 1000;
  const segRemaining = seg.minutes * 60 - (now - new Date(meeting.segment_started_at).getTime()) / 1000;
  const widget = seg.widget;

  return (
    <div data-testid="live-meeting" className="px-4 sm:px-6 lg:px-10 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">{meeting.template_name} · Live</span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">{seg.title}</h1>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="font-mono text-2xl font-bold text-cyan-700" data-testid="segment-timer">{mmss(segRemaining)}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-400">segment · total {mmss(elapsedTotal)}</div>
          </div>
          {cur < segs.length - 1 ? (
            <Button onClick={() => segMut.mutate(cur + 1)} className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="next-segment-btn">
              Next segment <ChevronRight size={15} className="ml-1" />
            </Button>
          ) : (
            <Button onClick={() => setConcluding(true)} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="conclude-btn">
              <Flag size={14} className="mr-1" /> Conclude
            </Button>
          )}
        </div>
      </div>
      <div className="grid lg:grid-cols-[250px_1fr_290px] gap-4 items-start">
        <Card className="rounded-sm border-slate-200 shadow-none p-2" data-testid="agenda-panel">
          {segs.map((s, i) => (
            <button key={i} onClick={() => segMut.mutate(i)} data-testid={`segment-btn-${i}`}
              className={`w-full flex items-center gap-2 text-left px-3 py-2 rounded-sm mb-0.5 ${i === cur ? "bg-slate-900 text-white" : i < cur ? "opacity-45" : "hover:bg-slate-50"}`}>
              <span className={`w-4 h-4 rounded-sm border flex-shrink-0 ${i < cur ? "bg-emerald-500 border-emerald-500" : i === cur ? "border-cyan-400" : "border-slate-300"}`} />
              <span className="flex-1 min-w-0">
                <span className={`block text-sm font-semibold ${i < cur ? "line-through" : ""}`}>{s.title}</span>
                {s.desc && <span className={`block text-[10.5px] ${i === cur ? "text-cyan-300" : "text-slate-400"}`}>{s.desc}</span>}
              </span>
              <span className={`font-mono text-[11px] ${i === cur ? "text-white" : "text-slate-400"}`}>{s.minutes}m</span>
            </button>
          ))}
        </Card>
        <Card className="rounded-sm border-slate-200 shadow-none p-4 min-h-[300px]" data-testid="segment-content">
          {widget === "scorecard" && <ScorecardWidget />}
          {widget === "rocks" && <RocksWidget />}
          {widget === "todos" && <TodosWidget />}
          {widget === "ids" && <IdsWidget />}
          {widget === "conclude" && (
            <div data-testid="widget-conclude">
              <div className="text-xs text-slate-500 mb-3">Recap the to-dos on the right, agree what cascades to the team, then hit Conclude to rate the meeting.</div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Cascading messages / notes</label>
              <Textarea rows={5} value={notes} onChange={(e) => setNotes(e.target.value)}
                onBlur={() => api.patch(`/mgmt/meetings/${meeting.id}`, { notes }).catch(() => {})}
                className="rounded-sm mt-1" data-testid="meeting-notes-input" placeholder="What does the rest of the team need to hear?" />
            </div>
          )}
          {widget === "none" && (
            <div data-testid="widget-none">
              <div className="text-xs text-slate-500 mb-2 flex items-center gap-1.5">
                <Timer size={14} className="text-slate-300" />
                {seg.desc || "Discussion segment — keep it tight."} Notes typed here are captured in the minutes.
              </div>
              <Textarea rows={9} value={segNotes[cur] || ""}
                onChange={(e) => setSegNotes((n) => ({ ...n, [cur]: e.target.value }))}
                onBlur={() => api.patch(`/mgmt/meetings/${meeting.id}`, { segment_notes: segNotes }).catch(() => {})}
                placeholder="What was discussed…" className="rounded-sm" data-testid="segment-notes-input" />
            </div>
          )}
        </Card>
        <div className="space-y-4">
          <Card className="rounded-sm border-slate-200 shadow-none" data-testid="meeting-todos-panel">
            <div className="px-3 py-2 border-b border-slate-200 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">To-Dos this meeting · 7-day</div>
            <div className="p-3 space-y-2">
              <p className="text-[10.5px] text-slate-400" data-testid="todo-sync-hint">Each to-do also lands in the owner's Daily Tasks planner — ticking it off there syncs back here.</p>
              {(mtgTodos?.open || []).concat(mtgTodos?.done || []).map((t) => (
                <div key={t.id} className="text-sm border-b border-slate-100 pb-1.5" data-testid={`captured-todo-${t.id}`}>
                  <div className={`font-medium ${t.status === "done" ? "line-through text-slate-400" : "text-slate-800"}`}>{t.title}</div>
                  <div className="text-[10.5px] text-slate-400">{t.owner_name} · <span className="font-mono text-cyan-700">due {fmtD(t.due_date)}</span></div>
                </div>
              ))}
              <Input placeholder="New to-do…" value={todoForm.title} onChange={(e) => setTodoForm((f) => ({ ...f, title: e.target.value }))} className="rounded-sm h-8 text-sm" data-testid="todo-title-input" />
              <div className="flex gap-2">
                <Select value={todoForm.owner_id} onValueChange={(v) => {
                  const m = (team || []).find((t) => t.id === v);
                  setTodoForm((f) => ({ ...f, owner_id: v, owner_name: m?.name || "" }));
                }}>
                  <SelectTrigger className="rounded-sm h-8 text-xs flex-1" data-testid="todo-owner-select"><SelectValue placeholder="Owner" /></SelectTrigger>
                  <SelectContent>{(team || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 rounded-sm h-8" data-testid="add-todo-btn"
                  disabled={!todoForm.title.trim() || !todoForm.owner_name || todoMut.isPending}
                  onClick={() => todoMut.mutate()}><Plus size={14} /></Button>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <Dialog open={concluding} onOpenChange={setConcluding}>
        <DialogContent className="max-w-md rounded-sm max-h-[90vh] overflow-y-auto" data-testid="conclude-dialog">
          <DialogHeader><DialogTitle>Conclude — rate the meeting 1–10</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {(team || []).map((t) => (
              <div key={t.id} className="flex items-center gap-3">
                <span className="flex-1 text-sm text-slate-700">{t.name}</span>
                <Input type="number" min={1} max={10} placeholder="—" value={ratings[t.id] || ""}
                  onChange={(e) => setRatings((r) => ({ ...r, [t.id]: e.target.value }))}
                  className="rounded-sm w-20 h-8 font-mono" data-testid={`rating-input-${t.id}`} />
              </div>
            ))}
            <div className="pt-1">
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Notes / cascading messages</label>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm mt-1" data-testid="conclude-notes-input" />
            </div>
            <p className="text-[11px] text-slate-400">Minutes, ratings & to-dos are saved — doubles as your management-review record.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setConcluding(false)}>Keep meeting</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="conclude-confirm-btn"
              disabled={concludeMut.isPending} onClick={() => concludeMut.mutate()}>Save & End</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TemplateBuilder({ open, template, onClose }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [segs, setSegs] = useState([{ title: "", desc: "", minutes: 10, widget: "none" }]);
  useEffect(() => {
    if (open) {
      setName(template?.name || "");
      setSegs(template?.segments?.length ? template.segments.map((s) => ({ ...s })) : [{ title: "", desc: "", minutes: 10, widget: "none" }]);
    }
  }, [open, template]);
  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = { name, segments: segs.filter((s) => s.title.trim()) };
      if (template?.id) return (await api.patch(`/mgmt/meeting-templates/${template.id}`, payload)).data;
      return (await api.post("/mgmt/meeting-templates", payload)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["mgmt-templates"] }); onClose(); toast.success("Agenda saved"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not save agenda"),
  });
  const upd = (i, p) => setSegs((arr) => arr.map((s, j) => (j === i ? { ...s, ...p } : s)));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader><DialogTitle>{template?.id ? `Edit agenda — ${template.name}` : "Build a custom agenda"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Agenda name — e.g. Quarterly Pulse" value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="template-name-input" />
          {segs.map((s, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`template-seg-${i}`}>
              <Input placeholder="Segment title" value={s.title} onChange={(e) => upd(i, { title: e.target.value })} className="rounded-sm h-8 text-sm flex-1" data-testid={`template-seg-title-${i}`} />
              <Input type="number" min={1} max={180} value={s.minutes} onChange={(e) => upd(i, { minutes: Number(e.target.value) })} className="rounded-sm h-8 w-16 font-mono text-sm" data-testid={`template-seg-min-${i}`} />
              <Select value={s.widget} onValueChange={(v) => upd(i, { widget: v })}>
                <SelectTrigger className="rounded-sm h-8 text-xs w-36" data-testid={`template-seg-widget-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{WIDGET_OPTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => setSegs((a) => a.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" data-testid={`template-seg-remove-${i}`}><X size={14} /></button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="rounded-sm text-xs" data-testid="template-add-seg-btn"
            onClick={() => setSegs((a) => [...a, { title: "", desc: "", minutes: 10, widget: "none" }])}><Plus size={13} className="mr-1" />Add segment</Button>
          <div className="text-[11px] text-slate-400">Total: {segs.reduce((s, x) => s + (Number(x.minutes) || 0), 0)} min</div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" data-testid="template-save-btn"
            disabled={!name.trim() || !segs.some((s) => s.title.trim()) || saveMut.isPending} onClick={() => saveMut.mutate()}>{template?.id ? "Save changes" : "Save agenda"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MeetingAttachments({ meetingId }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const { data: files = [] } = useQuery({
    queryKey: ["mtg-files", meetingId],
    queryFn: async () => (await api.get(`/mgmt/meetings/${meetingId}/attachments`)).data,
    enabled: !!meetingId,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["mtg-files", meetingId] });
  const upload = useMutation({
    mutationFn: async (fileList) => {
      for (const f of fileList) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/mgmt/meetings/${meetingId}/attachments`, fd);
      }
    },
    onSuccess: () => { toast.success("Attached to minutes"); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });
  const del = useMutation({
    mutationFn: async (fid) => api.delete(`/mgmt/meetings/${meetingId}/attachments/${fid}`),
    onSuccess: refresh,
  });
  const viewFile = async (fid) => {
    try {
      const res = await api.get(`/mgmt/meetings/${meetingId}/attachments/${fid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open file"); }
  };
  return (
    <div data-testid="meeting-attachments">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Attachments — reports reviewed</div>
        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={upload.isPending}
          className="rounded-sm h-7 text-xs" data-testid="meeting-upload-btn">
          {upload.isPending ? <Loader2 size={12} className="animate-spin mr-1" /> : <Paperclip size={12} className="mr-1" />}Attach
        </Button>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.docx,.csv" multiple className="hidden"
          onChange={(e) => { if (e.target.files?.length) upload.mutate(Array.from(e.target.files)); e.target.value = ""; }}
          data-testid="meeting-file-input" />
      </div>
      {files.length > 0 ? (
        <div className="mt-1.5 border border-slate-200 rounded-sm divide-y divide-slate-100">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-2 px-2.5 py-1.5 text-xs" data-testid={`meeting-file-${f.id}`}>
              <FileText size={13} className="text-slate-400 shrink-0" />
              <button type="button" onClick={() => viewFile(f.id)}
                className="font-medium text-slate-800 hover:text-cyan-700 hover:underline truncate"
                data-testid={`meeting-file-open-${f.id}`}>{f.label || f.filename}</button>
              <span className="text-slate-400 shrink-0">{Math.round((f.size || 0) / 1024)} KB</span>
              <button type="button" onClick={() => window.confirm("Remove this attachment?") && del.mutate(f.id)}
                className="ml-auto text-slate-300 hover:text-rose-600" data-testid={`meeting-file-delete-${f.id}`}><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-slate-400 mt-1">No files yet — attach the reports that were reviewed (PDF, image, Excel, Word, CSV).</p>
      )}
    </div>
  );
}

const ROCK_SNAP_CLS = { on_track: "bg-emerald-100 text-emerald-700", off_track: "bg-red-100 text-red-700", done: "bg-slate-200 text-slate-700" };

export default function ManagementMeetings() {
  const qc = useQueryClient();
  const [tplId, setTplId] = useState(null);
  const [building, setBuilding] = useState(null); // null | {} new | template to edit
  const [viewing, setViewing] = useState(null);

  const { data } = useQuery({ queryKey: ["mgmt-meetings"], queryFn: async () => (await api.get("/mgmt/meetings")).data, refetchInterval: 30000 });
  const { data: templates } = useQuery({ queryKey: ["mgmt-templates"], queryFn: async () => (await api.get("/mgmt/meeting-templates")).data });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/auth/team")).data });
  const { data: viewTodos } = useQuery({
    queryKey: ["mtg-todos", viewing?.id],
    queryFn: async () => (await api.get(`/mgmt/todos?meeting_id=${viewing.id}`)).data,
    enabled: !!viewing,
  });

  const startMut = useMutation({
    mutationFn: async () => (await api.post("/mgmt/meetings/start", { template_id: tplId || templates?.[0]?.id })).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["mgmt-meetings"] }); if (d.existing) toast.info("A meeting is already running — joining it"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Could not start meeting"),
  });
  const delTplMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/mgmt/meeting-templates/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["mgmt-templates"] }),
    onError: (e) => toast.error(e.response?.data?.detail || "Could not delete agenda"),
  });

  if (data?.live) {
    return (
      <div data-testid="management-meetings-page" className="pb-24">
        <LiveMeeting meeting={data.live} team={team} />
      </div>
    );
  }

  const selected = tplId || templates?.[0]?.id;
  return (
    <div data-testid="management-meetings-page" className="pb-24">
      <PageHeader
        title="Meeting OS"
        subtitle="Timed agendas that pull in your live Scorecard, Rocks and Issues · minutes & ratings saved as management-review records"
        actions={
          <>
            <Button variant="outline" className="rounded-sm" onClick={() => setBuilding({})} data-testid="build-agenda-btn"><Plus size={14} className="mr-1" />Custom agenda</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700 rounded-sm" onClick={() => startMut.mutate()} disabled={!selected || startMut.isPending} data-testid="start-meeting-btn">
              <Play size={14} className="mr-1" />Start Meeting
            </Button>
          </>
        }
      />
      <div className="px-4 sm:px-6 lg:px-10">
      <div className="flex flex-wrap gap-2 mb-5" data-testid="template-picker">
        {(templates || []).map((t) => (
          <button key={t.id} onClick={() => setTplId(t.id)} data-testid={`template-card-${t.id}`}
            className={`text-left border rounded-sm px-4 py-3 min-w-[200px] ${selected === t.id ? "border-cyan-600 ring-1 ring-cyan-600 bg-cyan-50/40" : "border-slate-200 bg-white hover:border-slate-400"}`}>
            <div className="flex items-center justify-between gap-3">
              <span className="font-semibold text-sm text-slate-900">{t.name}</span>
              {!t.builtin && (
                <span className="flex items-center gap-1.5 shrink-0">
                  <span role="button" tabIndex={0} className="text-slate-300 hover:text-cyan-700" data-testid={`template-edit-${t.id}`}
                    onClick={(e) => { e.stopPropagation(); setBuilding(t); }}><Pencil size={12} /></span>
                  <span role="button" tabIndex={0} className="text-slate-300 hover:text-red-600" data-testid={`template-delete-${t.id}`}
                    onClick={(e) => { e.stopPropagation(); window.confirm("Delete this agenda?") && delTplMut.mutate(t.id); }}><X size={13} /></span>
                </span>
              )}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">{t.segments.length} segments · {t.segments.reduce((s, x) => s + x.minutes, 0)} min</div>
          </button>
        ))}
      </div>
      <Card className="rounded-sm border-slate-200 shadow-none overflow-hidden" data-testid="past-meetings">
        <div className="px-4 py-2.5 border-b border-slate-200 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Past meetings · minutes archive</div>
        {(data?.past || []).length === 0 ? (
          <div className="px-4 py-8 text-sm text-slate-400" data-testid="past-empty">No meetings yet — start your first Level 10.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="text-left px-4 py-2 font-semibold">Date</th>
                <th className="text-left px-2 py-2 font-semibold">Agenda</th>
                <th className="text-center px-2 py-2 font-semibold">Duration</th>
                <th className="text-center px-2 py-2 font-semibold">To-Dos</th>
                <th className="text-center px-2 py-2 font-semibold">Issues solved</th>
                <th className="text-center px-2 py-2 font-semibold">Rating</th>
              </tr>
            </thead>
            <tbody>
              {(data?.past || []).map((m) => (
                <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setViewing(m)} data-testid={`past-meeting-${m.id}`}>
                  <td className="px-4 py-2.5 font-mono text-xs">{fmtD(m.date)}</td>
                  <td className="px-2 py-2.5 font-medium text-slate-800">{m.template_name}</td>
                  <td className="px-2 py-2.5 text-center font-mono text-xs text-slate-500">{m.duration_min}m</td>
                  <td className="px-2 py-2.5 text-center font-mono text-xs">{m.todos_count ?? 0}</td>
                  <td className="px-2 py-2.5 text-center font-mono text-xs">{m.issues_solved ?? 0}</td>
                  <td className="px-2 py-2.5 text-center">
                    <span className={`font-mono text-xs font-bold px-2 py-0.5 rounded-sm ${m.avg_rating >= 8 ? "bg-emerald-100 text-emerald-700" : m.avg_rating ? "bg-amber-100 text-amber-700" : "text-slate-300"}`}>{m.avg_rating ?? "—"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      </div>

      <TemplateBuilder open={!!building} template={building?.id ? building : null} onClose={() => setBuilding(null)} />

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto" data-testid="minutes-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckSquare size={16} className="text-cyan-600" />Minutes — {viewing?.template_name} · {viewing && fmtD(viewing.date)}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-4 gap-2 text-center">
                {[["Duration", `${viewing.duration_min}m`], ["To-Dos", viewing.todos_count ?? 0], ["Issues solved", viewing.issues_solved ?? 0], ["Rating", viewing.avg_rating ?? "—"]].map(([k, v]) => (
                  <div key={k} className="border border-slate-200 rounded-sm p-2"><div className="text-[9px] uppercase tracking-wider text-slate-400 font-bold">{k}</div><div className="font-mono font-bold">{v}</div></div>
                ))}
              </div>

              {viewing.segment_notes && Object.values(viewing.segment_notes).some((v) => v?.trim()) && (
                <div data-testid="minutes-segment-notes">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Discussion notes</div>
                  {Object.entries(viewing.segment_notes).filter(([, v]) => v?.trim()).map(([k, v]) => (
                    <div key={k} className="mb-2">
                      <div className="text-xs font-semibold text-slate-700">{viewing.segments?.[Number(k)]?.title || `Segment ${Number(k) + 1}`}</div>
                      <p className="text-xs text-slate-600 whitespace-pre-wrap">{v}</p>
                    </div>
                  ))}
                </div>
              )}

              {viewing.snapshot && (
                <div className="space-y-4" data-testid="minutes-snapshot">
                  <div data-testid="minutes-scorecard">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Scorecard as reviewed — week of {fmtD(viewing.snapshot.week_start)}</div>
                    {(viewing.snapshot.scorecard || []).map((s, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-xs py-1 border-b border-slate-100">
                        <span className="truncate text-slate-700">{s.name}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="font-mono text-slate-400">{s.goal_op === "lte" ? "≤" : "≥"} {fmtVal(s.goal, s.unit)}</span>
                          <span className={`font-mono px-1.5 py-0.5 rounded-sm min-w-[52px] text-center ${s.value == null ? "text-slate-300" : s.hit ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{fmtVal(s.value, s.unit)}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                  <div data-testid="minutes-rocks">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Rocks — {viewing.snapshot.quarter}</div>
                    {(viewing.snapshot.rocks || []).map((r, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-xs py-1 border-b border-slate-100">
                        <span className="truncate text-slate-700">{r.title}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-slate-400">{r.owner_name}</span>
                          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${ROCK_SNAP_CLS[r.status] || "bg-slate-100 text-slate-500"}`}>{(r.status || "").replace("_", " ")}</span>
                        </span>
                      </div>
                    ))}
                    {(viewing.snapshot.rocks || []).length === 0 && <div className="text-xs text-slate-400">No rocks this quarter.</div>}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div data-testid="minutes-issues">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Open issues at close</div>
                      {(viewing.snapshot.issues_open || []).map((x, i) => (
                        <div key={i} className="text-xs py-0.5 border-b border-slate-100 flex justify-between gap-2">
                          <span className="truncate"><span className="font-mono text-cyan-700">{x.number}</span> {x.title}</span>
                          <span className="font-mono text-slate-400 shrink-0">▲{x.votes}</span>
                        </div>
                      ))}
                      {(viewing.snapshot.issues_open || []).length === 0 && <div className="text-xs text-slate-400">Issues list clear.</div>}
                      {(viewing.snapshot.issues_solved || []).length > 0 && (
                        <div className="mt-1.5">
                          <div className="text-[9px] uppercase tracking-wider font-bold text-emerald-600">Solved in this meeting</div>
                          {viewing.snapshot.issues_solved.map((x, i) => (
                            <div key={i} className="text-xs py-0.5 text-slate-500"><span className="font-mono">{x.number}</span> {x.title}{x.solution ? ` → ${x.solution}` : ""}</div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div data-testid="minutes-todos-snapshot">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">To-Dos open at close</div>
                      {(viewing.snapshot.todos_open || []).map((t, i) => (
                        <div key={i} className="text-xs py-0.5 border-b border-slate-100 flex justify-between gap-2">
                          <span className="truncate">{t.title}</span>
                          <span className="text-slate-400 shrink-0">{t.owner_name} · {fmtD(t.due_date)}</span>
                        </div>
                      ))}
                      {(viewing.snapshot.todos_open || []).length === 0 && <div className="text-xs text-slate-400">None open.</div>}
                      {(viewing.snapshot.todos_done || []).length > 0 && (
                        <div className="mt-1.5">
                          <div className="text-[9px] uppercase tracking-wider font-bold text-emerald-600">Ticked done in this meeting</div>
                          {viewing.snapshot.todos_done.map((t, i) => (
                            <div key={i} className="text-xs py-0.5 text-slate-400 line-through">{t.title}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {(viewing.ratings || []).length > 0 && (
                <div><div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Ratings</div>
                  {viewing.ratings.map((r, i) => <div key={i} className="flex justify-between text-xs py-0.5 border-b border-slate-100"><span>{r.name}</span><span className="font-mono">{r.rating}/10</span></div>)}
                </div>
              )}
              <div><div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">To-Dos captured this meeting</div>
                {[...(viewTodos?.open || []), ...(viewTodos?.done || [])].map((t) => (
                  <div key={t.id} className="flex justify-between text-xs py-0.5 border-b border-slate-100">
                    <span className={t.status === "done" ? "line-through text-slate-400" : ""}>{t.title}</span>
                    <span className="text-slate-400">{t.owner_name}</span>
                  </div>
                ))}
                {(viewTodos?.open || []).length + (viewTodos?.done || []).length === 0 && <div className="text-xs text-slate-400">None recorded.</div>}
              </div>
              {viewing.notes && <div><div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Notes / cascade</div><p className="text-xs text-slate-600 whitespace-pre-wrap">{viewing.notes}</p></div>}

              <MeetingAttachments meetingId={viewing.id} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
